import './styles.scss'

import React, { useEffect, useCallback, useState } from "react"
import { useSelector, useDispatch } from 'react-redux'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Link,
  PDFViewer,
  Font,
  Image,
  Svg,
  G,
  Path,
  Defs,
  Polygon,
  Line,
  ClipPath,
  Rect
} from "@react-pdf/renderer"

import LoadingSpinner from '../../components/common/LoadingSpinner'
import { get_decoded_string } from '../../plugins/helper'

import moment from 'moment'

import {
  selectCurrentProject,
} from '../../store/projectSlice'

import {
  fetch_project,
  fetch_product_customizations,
} from '../../store/asyncThunk'

moment.locale('fi')

Font.register({
  family: "Inter Tight",
  src: "/fonts/InterTight-ExtraBold.ttf",
  fontWeight: "bold",
})

Font.register({
  family: "Libre Franklin",
  src: "/fonts/LibreFranklin-Regular.ttf",
})

Font.register({
  family: "Libre Franklin",
  src: "/fonts/LibreFranklin-Bold.ttf",
  fontWeight: 700,
})

Font.register({
  family: "DM Mono",
  src: "/fonts/DMMono-Medium.ttf",
  fontWeight: 500,
})

// Styles for the PDF
const styles = StyleSheet.create({
  page: {
    display: "flex",
    flexDirection: "column",
    padding: 30,
    paddingTop: 80,
    paddingBottom: 60,
  },
  content: {
    marginBottom: 30,
  },
  projectTitle: {
    fontFamily: "Inter Tight",
    fontSize: 40,
    fontWeight: "bold",
  },
  projectDescription: {
    fontFamily: "Libre Franklin",
    fontSize: 12,
    marginTop: 10,
    lineHeight: '140%',
    width: 592,
  },
  headingTextLeft: {
    fontFamily: "Libre Franklin",
    fontSize: 11,
    lineHeight: '140%',
    position: "absolute",
    top: 20,
    left: 30,
    minWidth: 120,
  },
  headingTextMiddle: {
    fontFamily: "Libre Franklin",
    fontSize: 11,
    lineHeight: '140%',
    top: 20,
    left: 0,
    right: 0,
    position: "absolute",
    textAlign: "center",
  },
  headingTextRight: {
    fontFamily: "Libre Franklin",
    fontSize: 11,
    lineHeight: '140%',
    position: "absolute",
    top: 20,
    right: 30,
  },
  footer: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    position: "absolute",
    bottom: 0,
    left: 0,
    fontSize: 11,
    width: "100%",
    backgroundColor: "#F2F0E4",
    padding: '20px 20px',
    paddingTop: '25px'
  },
  footerContactContainerLeft: {
    display: "flex",
    flexDirection: "column",
    marginLeft: 10,
  },
  footerContactContainerMiddle: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  footerContactContainerRight: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    marginRight: 10,
  },
  footerContactWrapper: {
    display: "flex",
    flexDirection: "row",
    gap: '5px',
  },
  textBold: {
    fontFamily: "Libre Franklin",
    fontSize: "11px",
    fontWeight: 700,
  },
  text: {
    fontFamily: "Libre Franklin",
    fontSize: "11px",
  },
  textWrap: {
    fontFamily: "Libre Franklin",
    fontSize: "11px",
  },
  logo: {
    position: "absolute",
    bottom: 10,
    left: 0,
    fontSize: 11,
    width: 120
  },
  link: {
    color : "#000",
  },
  productEntryContainer: {
    display: "flex",
    flexDirection: "column",
  },
  productEntry: {
    display: "flex",
    flexDirection: "row",
  },
  productEntryDivider: {
    width: "100%",
    height: 1,
    backgroundColor: "rgba(0, 0, 0, 0.40)",
    marginTop: 20,
    marginBottom: 20,
  },
  productEntryLeftContent: {
    display: "flex",
    flexDirection: "row",
    width: "calc(85% - 1rem)",
  },
  productEntryLeftContentProductInfo: {
    paddingLeft: '1rem',
    paddingLeft: '20px',
  },
  productEntryLeftContentProductInfoTitle: {
    fontFamily: "Inter Tight",
    fontSize: '22px',
    fontWeight: "bold",
    lineHeight: '110%',
    marginBottom: 10,
  },
  productEntryLeftContentProductInfoCustomisation: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10
  },
  productEntryLeftContentProductInfoCustomisationEntry: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    width: '49%',
  },
  productEntryLeftContentProductInfoCustomisationEntryDescription: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start"
  },
  productEntryLeftContentProductInfoCustomisationEntryText: {
    fontFamily: "DM Mono",
    fontWeight: 500,
    fontSize: 10,
    color: "#000",
    textTransform: "uppercase",
  },
  productEntryLeftContentProductInfoCustomisationEntryTextPurple: {
    fontFamily: "DM Mono",
    fontWeight: 500,
    fontSize: 10,
    color: "#8E5CD9",
    textTransform: "uppercase",
  },
  productEntryLeftContentProductInfoCustomisationEntryImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: 2.9,
  },
  productEntryLeftContentProductInfoCustomisationEntryImgWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
    width: 26,
    height: 26,
    border: "1 solid #000",
    marginRight: 10
  },
  productEntryRightContent: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    width: "calc(15% - 1rem)",
    minWidth: '140px',

  },
  productEntryRightContentTitle: {
    fontSize: 18,
    fontFamily: "Inter Tight",
    fontWeight: "bold",
    lineHeight: '110%',
    marginBottom: 3,
    marginTop: 2,
  },
  productEntryRightContentSpaceContainer: {
    display: "flex",
    flexDirection: "row",
    gap: 5,
  },
  productEntryRightContentSpaceText: {
    fontSize: 10,
    fontFamily: "DM Mono",
    fontWeight: 500,
    lineHeight: '100%',
    textTransform: "uppercase",
    color: '#9F9F9F',
  },
  productEntryImg: {
    width: "148px",
    height: "101px",
    objectFit: "cover",
    borderRadius: "10px",
  },
  newPageMargin: {
    marginTop: 20,
  },
  productSpaceContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    position: "relative",
    marginTop: 20,
    marginBottom: 20,
  },
  productSpaceLine: {
    height: 1,
    width: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.40)",
    position: "absolute",
    top: "50%",
    left: 0,
  },
  productSpaceText: {
    fontFamily: "DM Mono",
    fontSize: 12,
    fontWeight: 500,
    textTransform: "uppercase",
    color: "#000",
    lineHeight: '110%',
    padding: "10px 20px",
    borderRadius: 100,
    backgroundColor: "#FCEA19",
  },
})

const ProductSummaryPDF = () => {
  const dispatch = useDispatch()
  const { project_id } = useParams()
  const current_project = useSelector((state) => state.project.current_project)
  const [searchParams] = useSearchParams()
  const group_by = searchParams.get('group_by')
  const [productGroups, setProductGroups] = useState({})
  const formattedDate = moment().format('DD.MM.YYYY, HH:mm')
  const { t, i18n } = useTranslation()
  const customizations = useSelector((state) => state.product.customizations)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (project_id) {
      dispatch(selectCurrentProject(parseInt(project_id)))
    }
  }, [dispatch, project_id])

  const get_resources = useCallback(async () => {
    if (project_id) {
      setIsLoading(true)
      await Promise.all([
        dispatch(fetch_project({ project_id: parseInt(project_id) })),
        dispatch(fetch_product_customizations(true))
      ])
      dispatch(selectCurrentProject(parseInt(project_id)))
      setIsLoading(false)
    }
  }, [project_id, dispatch])

  useEffect(() => {
    if (!current_project) {
      get_resources()
    }
  }, [get_resources, project_id])

  useEffect(() => {
    if (group_by && current_project) {
      //GROUP BY PRODUCT
      if (group_by === '1') {
        let product_list = current_project.products.map((p) => ({
          ...p,
          quantity: get_product_quantity(p),
          spaces: get_product_space(p),
        }))
        product_list = product_list.sort((a, b) => {
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            ? -1
            : 1
        })
        const grouped = groupProducts(product_list).sort((a, b) => a.name.localeCompare(b.name))
        setProductGroups(grouped)
      }
      //GROUP BY space
      if (group_by === '2') {
        let product_list = current_project.products.map((p) => ({
          ...p,
          quantity: get_product_quantity(p),
          spaces: get_product_space(p),
        }))
        product_list = product_list.sort((a, b) => {
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            ? -1
            : 1
        })
        const grouped = groupBySpaces(product_list)
                          .sort((a, b) => a.space.id - b.space.id) // sort spaces by id
                          .map(group => ({
                            ...group,
                            products: group.products.sort((a, b) => t(`${get_decoded_string(a.name)}.name`).localeCompare(t(`${get_decoded_string(b.name)}.name`))) // sort products by name
                          }))
        setProductGroups(grouped)
      }
    }
  }, [group_by, current_project])

  const groupProducts = (products) => {
    const grouped = []

    for (const product of products) {
      // Create a unique key to group products by product_id and customization
      const key = `${product.product_id}::${product.customizations
        .map(c => `${c.customization_id}-${c.selected_option_id}-${c.value ?? ''}`)
        .sort() // ensures order doesn't affect grouping
        .join('|')}`
  
      // Try to find an existing group with this key
      const existing = grouped.find(item => item._groupKey === key)
  
      if (existing) {
        // Merge spaces, checking for matching `id`s to combine quantities
        product.spaces.forEach(space => {
          const existingSpace = existing.spaces.find(s => s.id === space.id)
          if (existingSpace) {
            // If the space already exists, add the quantity
            existingSpace.quantity += space.quantity
          } else {
            // Otherwise, push the new space into the spaces array
            existing.spaces.push({ ...space })
          }
        })
      } else {
        // Clone product and include a new spaces array
        grouped.push({
          ...product,
          spaces: [...product.spaces], // copy to avoid mutations
          _groupKey: key
        })
      }
    }
  
    // Remove the internal _groupKey before returning
    return grouped.map(({ _groupKey, ...rest }) => rest)
  }

  const groupBySpaces = (products) => {
    const grouped = []
  
    for (const product of products) {
      for (const space of product.spaces) {
        const spaceKey = space.id
  
        // Find or create the space group
        let spaceGroup = grouped.find(g => g.space.id === spaceKey)
        if (!spaceGroup) {
          spaceGroup = {
            space: { ...space },
            products: []
          }
          grouped.push(spaceGroup)
        }
  
        // Create a key for this product (based on product_id + customizations)
        const productKey = `${product.product_id}::${(product.customizations || [])
          .map(c => `${c.customization_id}-${c.selected_option_id}`)
          .sort()
          .join('|')}`
  
        // Find if this product already exists in the current space group
        const existingProduct = spaceGroup.products.find(
          p => p._groupKey === productKey
        )
  
        if (existingProduct) {
          existingProduct.quantity += space.quantity
        } else {
          spaceGroup.products.push({
            ...product,
            quantity: space.quantity,
            _groupKey: productKey
          })
        }
      }
    }
  
    // Strip _groupKey before returning
    return grouped.map(spaceGroup => ({
      ...spaceGroup,
      products: spaceGroup.products.map(({ _groupKey, ...rest }) => rest)
    }))
  }

  function areCustomizationsEqual(a = [], b = []) {
    if (a.length !== b.length) return false
  
    const sortById = (arr) =>
      [...arr].sort((x, y) => x.customization_id - y.customization_id)
  
    const sortedA = sortById(a)
    const sortedB = sortById(b)
  
    return sortedA.every((item, index) =>
      item.customization_id === sortedB[index].customization_id &&
      item.selected_option_id === sortedB[index].selected_option_id
    )
  }

  const get_product_quantity = (product) => {
    let result = 0
    const assigned_spaces = current_project.spaces.filter((s) =>
      s.assigned_products.some((pro) => pro.id === product.id)
    )
    assigned_spaces.forEach((s) => {
      s.assigned_products.forEach((p) => {
        if (p.id === product.id) {
          result += p.quantity
        }
      })
    })
    return result
  }

  const get_product_space = (product) => {
    let list = []
    const assigned_spaces = current_project.spaces.filter((s) =>
      s.assigned_products.some((pro) => pro.id === product.id)
    )

    assigned_spaces.forEach((s) => {
      const assigned_product = s.assigned_products.find(
        (a) => a.id === product.id
      )

      if (assigned_product) {
        list.push({
          id: s.id,
          name: s.name,
          quantity: assigned_product.quantity,
        })
      }
    })
    return list
  }

  const get_selected_customization_image = (customization_id, selected_id, type=null) => {
    const customization = customizations.find((c) => c.id === customization_id)
    
    if (customization) {
      if (!type) {
        const option = customization.options.find((o) => o.id === selected_id)
        if (option) {
          return option.imgSrc ? option.imgSrcs[0] : option.id
        }
      } else if (type === 'viherpysäkki') {
        return customization.options[0].imgSrcs[0]
      }
    }
    return ''
  }

  function group_by_customization_id(customizations) {
    const grouped = []

    customizations.forEach((item) => {
      // Check if an entry for the current customization_id already exists in the grouped array
      let existingGroup = grouped.find(group => group.customization_id === item.customization_id)
  
      if (existingGroup) {
        // If a group exists, add the customization_id, selected_option_id, and value to its "selected" array
        existingGroup.selected.push({
          customization_id: item.customization_id,
          selected_option_id: item.selected_option_id,
          value: item.value
        })
      } else {
        // If no group exists, create a new one and include the "selected" array
        grouped.push({
          customization_id: item.customization_id,
          date_created: item.date_created,
          date_modified: item.date_modified,
          id: item.id,
          name: item.name,
          selected: item.selected_option_id
            ? [{
                customization_id: item.customization_id,
                selected_option_id: item.selected_option_id,
                value: item.value
              }]
            : []
        })
      }
    })
    return grouped
  }

  const get_selected_customization = (customization, selected_id, config=null, textIndex=null) => {
    if (config) {
      if (config.type === 'räätälöitävä_viherseinä') {
        if (config.configType === 'width') {
          return (customization.value * config.value) + ' MM'
        } else if (config.configType === 'height') {
          return (customization.value * config.value + 400) + ' MM'
        }
      } else if (config.type === 'räätälöitävä_sammalseinä') {
        if (config.configType === 'custom') {
          return (customization.value)
        } else {
          return (customization.value) + ' MM'
        }
      } else if (config.type === 'viherpysäkki') {
        return (customization.value) + ' X ' + config.text[textIndex].toUpperCase()
      } else if (config.name === 'viherseinä') {
        return (
          <>
            <Text style={styles.productEntryLeftContentProductInfoCustomisationEntryText}>
              {`${customization.selected[0].value} X 1200 MM`}
            </Text>
            <Text style={styles.productEntryLeftContentProductInfoCustomisationEntryText}>
            {`${customization.selected[1].value} X 1800 MM`}
            </Text>
          </>
        )

      }
    }
    const selected_customization = customizations.find((c) => c.id === customization.customization_id)
    if (selected_customization) {
      const option = selected_customization.options.find((o) => o.id === selected_id)
      if (option) {
        if (get_decoded_string(selected_customization.name)=== 'korkeus') {
          return (t(`products.${get_decoded_string(selected_customization.name)}_options.k${get_decoded_string(option.name)}`)).toUpperCase()
        } 
        else if (get_decoded_string(selected_customization.name) === 'leveys') {
          return (t(`products.${get_decoded_string(selected_customization.name)}_options.l${get_decoded_string(option.name)}`)).toUpperCase()
        }
        return (t(`products.${get_decoded_string(selected_customization.name)}_options.${get_decoded_string(option.name)}`)).toUpperCase()
      }
    } 
    return ''
  }

  const Header = () => (
    <>
      <Text fixed style={styles.headingTextLeft}>{formattedDate}</Text>
      <Text fixed style={styles.headingTextMiddle}>{current_project.name} - {group_by === '1' ? t('project.pdfSortedByProduct') : t('project.pdfSortedBySpace')}</Text>
      <Text fixed style={styles.headingTextRight} render={({ pageNumber, totalPages }) => (
        `${pageNumber} / ${totalPages}`
      )} />
    </>
  )

  const Footer = () => (
    <View style={styles.footer} fixed>
      <View style={styles.footerContactContainerLeft}>
        <Svg width="120" height="19" viewBox="0 0 120 19" fill="none" xmlns="http://www.w3.org/2000/svg">
          <G id="innogreen logo" clip-path="url(#clip0_3230_2000)">
            <Path id="Vector" d="M0 13.1728V0.338379H3.10163V13.1728H0Z" fill="black"/>
            <Path id="Vector_2" d="M15.1782 13.1728V6.49494C15.1782 5.26914 14.8846 4.35436 14.2881 3.7689C13.7008 3.18344 12.9025 2.88156 11.9114 2.88156C10.9203 2.88156 10.1312 3.17429 9.53471 3.7689C8.94742 4.35436 8.6446 5.26914 8.6446 6.49494V13.1728H5.54297V6.49494C5.54297 5.42465 5.69897 4.48243 6.00179 3.67742C6.30461 2.87241 6.7359 2.19547 7.29566 1.64661C7.85543 1.09774 8.5253 0.686086 9.3053 0.411651C10.0945 0.137217 10.9662 0 11.9206 0C12.8749 0 13.7467 0.137217 14.5267 0.402504C15.3067 0.66779 15.9766 1.07944 16.5363 1.62831C17.0961 2.17718 17.5182 2.85412 17.8302 3.66827C18.133 4.48243 18.289 5.42465 18.289 6.50409V13.182H15.1874L15.1782 13.1728Z" fill="black"/>
            <Path id="Vector_3" d="M30.1352 13.1728V6.49494C30.1352 5.26914 29.8416 4.35436 29.2451 3.7689C28.6578 3.18344 27.8595 2.88156 26.8684 2.88156C25.8774 2.88156 25.0882 3.17429 24.4917 3.7689C23.9045 4.35436 23.6016 5.26914 23.6016 6.49494V13.1728H20.5V6.49494C20.5 5.42465 20.656 4.48243 20.9588 3.67742C21.2616 2.87241 21.6929 2.19547 22.2527 1.64661C22.8125 1.09774 23.4823 0.686086 24.2623 0.411651C25.0515 0.137217 25.9233 0 26.8776 0C27.832 0 28.7037 0.137217 29.4837 0.402504C30.2637 0.66779 30.9336 1.07944 31.4933 1.62831C32.0531 2.17718 32.4752 2.85412 32.7872 3.66827C33.09 4.48243 33.246 5.42465 33.246 6.50409V13.182H30.1444L30.1352 13.1728Z" fill="black"/>
            <Path id="Vector_4" d="M48.9462 6.76023C48.9462 7.7299 48.7811 8.63553 48.4415 9.46798C48.102 10.3004 47.6248 11.014 47.01 11.6086C46.3952 12.2032 45.6611 12.6697 44.8077 13.0082C43.9543 13.3467 43.0274 13.5113 42.018 13.5113C41.0086 13.5113 40.0543 13.3467 39.2192 13.0082C38.375 12.6697 37.6501 12.2032 37.0261 11.6086C36.4112 11.014 35.9341 10.3004 35.5945 9.46798C35.255 8.63553 35.0898 7.73905 35.0898 6.76023C35.0898 5.78141 35.255 4.88493 35.5945 4.06163C35.9341 3.22918 36.4112 2.51565 37.0261 1.92104C37.6409 1.32643 38.375 0.859894 39.2192 0.521425C40.0543 0.164661 40.9903 0 42.018 0C43.0458 0 43.9543 0.164661 44.8077 0.50313C45.6611 0.841598 46.3952 1.30814 47.01 1.90274C47.6248 2.49735 48.102 3.21088 48.4415 4.04333C48.7811 4.87578 48.9462 5.77227 48.9462 6.74194V6.76023ZM42.018 10.6298C43.1651 10.6298 44.0827 10.273 44.771 9.55031C45.4592 8.82764 45.7987 7.90371 45.7987 6.76023C45.7987 5.61675 45.4592 4.68368 44.771 3.97015C44.0827 3.24747 43.1651 2.89071 42.018 2.89071C40.871 2.89071 39.9533 3.24747 39.2559 3.97015C38.5585 4.69283 38.2098 5.61675 38.2098 6.76023C38.2098 7.90371 38.5585 8.83678 39.2559 9.55031C39.9533 10.273 40.871 10.6298 42.018 10.6298Z" fill="black"/>
            <Path id="Vector_5" d="M61.5657 11.2244H61.5107C61.0977 11.883 60.5196 12.4044 59.7763 12.7795C59.033 13.1637 58.1796 13.3558 57.2069 13.3558C56.4453 13.3558 55.6745 13.2277 54.9128 12.9624C54.1512 12.7063 53.4629 12.3038 52.8665 11.7549C52.27 11.2061 51.7837 10.5291 51.4074 9.70582C51.0312 8.88252 50.8477 7.90371 50.8477 6.76023C50.8477 5.79056 51.0128 4.88493 51.3524 4.06163C51.6919 3.22918 52.1691 2.51565 52.7839 1.92104C53.3987 1.32643 54.1328 0.859894 54.977 0.521425C55.8305 0.164661 56.7664 0 57.785 0C58.8036 0 59.7213 0.164661 60.5747 0.493982C61.4281 0.823303 62.153 1.28984 62.7495 1.88445C63.3459 2.47906 63.8231 3.20173 64.1535 4.04333C64.493 4.88493 64.6582 5.82715 64.6582 6.86086V12.1666C64.6582 13.3924 64.4838 14.4444 64.1259 15.3134C63.768 16.1825 63.2817 16.8869 62.6669 17.4357C62.0521 17.9846 61.3271 18.3779 60.4921 18.6341C59.657 18.8811 58.7669 19.0091 57.8309 19.0091C57.0326 19.0091 56.2801 18.9268 55.5735 18.753C54.8669 18.5792 54.2338 18.3139 53.6556 17.948C53.0867 17.5821 52.5912 17.1247 52.1966 16.5667C51.7928 16.0087 51.49 15.35 51.2881 14.5908H54.4173C54.6742 15.1945 55.1055 15.6428 55.6928 15.9172C56.2801 16.1916 56.9959 16.3288 57.8309 16.3288C58.978 16.3288 59.8864 16.027 60.5563 15.4232C61.2262 14.8195 61.5565 13.7675 61.5565 12.2764V11.2335L61.5657 11.2244ZM61.5657 6.76023C61.5657 5.61675 61.2262 4.68368 60.538 3.97015C59.8497 3.24747 58.9321 2.89071 57.785 2.89071C56.638 2.89071 55.7203 3.24747 55.0229 3.97015C54.3255 4.69283 53.9768 5.61675 53.9768 6.76023C53.9768 7.90371 54.3255 8.80934 55.0229 9.47713C55.7203 10.1449 56.638 10.4742 57.785 10.4742C58.9321 10.4742 59.8497 10.1449 60.538 9.47713C61.2262 8.80934 61.5657 7.90371 61.5657 6.76023Z" fill="black"/>
            <Path id="Vector_6" d="M94.5922 7.84882V7.90371C94.8033 8.77275 95.2254 9.45883 95.8585 9.95282C96.4917 10.4559 97.2992 10.7029 98.2719 10.7029C99.6576 10.7029 100.676 10.3004 101.319 9.47713H104.631C104.145 10.7121 103.347 11.6909 102.236 12.4136C101.126 13.1454 99.8044 13.5022 98.2719 13.5022C97.2442 13.5022 96.3082 13.3375 95.4731 12.999C94.6289 12.6606 93.904 12.194 93.28 11.5994C92.6652 11.0048 92.188 10.2913 91.8484 9.45883C91.5089 8.62638 91.3438 7.7299 91.3438 6.75108C91.3438 5.77227 91.5089 4.87578 91.8484 4.05248C92.188 3.22003 92.6652 2.5065 93.28 1.91189C93.8948 1.31728 94.6289 0.850746 95.4731 0.512277C96.3082 0.164661 97.2442 0 98.2719 0C99.2997 0 100.208 0.164661 101.062 0.50313C101.915 0.841598 102.64 1.30814 103.236 1.90274C103.833 2.49735 104.301 3.21088 104.64 4.04333C104.98 4.87578 105.145 5.77227 105.145 6.74194V7.83052H94.583L94.5922 7.84882ZM98.2719 2.80838C97.3543 2.80838 96.5835 3.03707 95.9687 3.48532C95.3538 3.93356 94.9225 4.54646 94.6748 5.33317V5.38806H101.869V5.33317C101.621 4.55561 101.19 3.94271 100.566 3.48532C99.942 3.03707 99.1712 2.80838 98.2719 2.80838Z" fill="black"/>
            <Path id="Vector_7" d="M116.897 13.1728V6.49494C116.897 5.26914 116.603 4.35436 116.007 3.7689C115.42 3.18344 114.621 2.88156 113.63 2.88156C112.639 2.88156 111.85 3.17429 111.253 3.7689C110.666 4.35436 110.363 5.26914 110.363 6.49494V13.1728H107.262V6.49494C107.262 5.42465 107.418 4.48243 107.721 3.67742C108.023 2.87241 108.455 2.19547 109.014 1.64661C109.574 1.09774 110.244 0.686086 111.024 0.411651C111.813 0.137217 112.685 0 113.639 0C114.594 0 115.465 0.137217 116.245 0.402504C117.025 0.66779 117.695 1.07944 118.255 1.62831C118.815 2.17718 119.237 2.85412 119.549 3.66827C119.852 4.48243 120.008 5.42465 120.008 6.50409V13.182H116.906L116.897 13.1728Z" fill="black"/>
            <Path id="Vector_8" d="M79.1274 7.84882V7.90371C79.3384 8.77275 79.7605 9.45883 80.3937 9.95282C81.0269 10.4559 81.8344 10.7029 82.8071 10.7029C84.2019 10.7029 85.2113 10.3004 85.8537 9.47713H89.1664C88.68 10.7121 87.8817 11.6909 86.7713 12.4136C85.661 13.1454 84.3396 13.5022 82.8071 13.5022C81.7793 13.5022 80.8433 13.3375 80.0083 12.999C79.1641 12.6606 78.4391 12.194 77.8151 11.5994C77.2003 11.0048 76.7231 10.2913 76.3836 9.45883C76.0441 8.62638 75.8789 7.7299 75.8789 6.75108C75.8789 5.77227 76.0441 4.87578 76.3836 4.05248C76.7231 3.22003 77.2003 2.5065 77.8151 1.91189C78.43 1.31728 79.1641 0.850746 80.0083 0.512277C80.8433 0.164661 81.7793 0 82.8071 0C83.8349 0 84.7433 0.164661 85.5967 0.50313C86.4501 0.841598 87.1751 1.30814 87.7715 1.90274C88.368 2.49735 88.836 3.21088 89.1755 4.04333C89.5151 4.87578 89.6802 5.77227 89.6802 6.74194V7.83052H79.1182L79.1274 7.84882ZM82.8071 2.80838C81.8895 2.80838 81.1186 3.03707 80.5038 3.48532C79.889 3.93356 79.4577 4.54646 79.2099 5.33317V5.38806H86.4043V5.33317C86.1565 4.55561 85.7252 3.94271 85.1012 3.48532C84.4772 3.03707 83.7064 2.80838 82.8071 2.80838Z" fill="black"/>
            <Path id="Vector_9" d="M74.2935 2.88156C74.2935 2.88156 73.4493 2.88156 73.4309 2.88156C72.4399 2.88156 71.6507 3.17429 71.0542 3.7689C70.4669 4.35436 70.1641 5.26914 70.1641 6.49494V13.1728H67.0625V6.49494C67.0625 5.42465 67.2185 4.48243 67.5213 3.67742C67.8241 2.87241 68.2554 2.19547 68.8152 1.64661C69.375 1.09774 70.0448 0.686086 70.8248 0.411651C71.6048 0.137217 72.4766 0 73.4309 0C73.4585 0 74.2752 0 74.2935 0V2.88156Z" fill="black"/>
          </G>
          <Defs>
            <ClipPath id="clip0_3230_2000">
              <Rect width="120" height="19" fill="white"/>
            </ClipPath>
          </Defs>
        </Svg>
      </View>

      <View style={styles.footerContactContainerMiddle}>
        <View style={styles.footerContactWrapper}>
          <Text style={styles.textBold} >{`${t('common.greenInterior')}:`}</Text>
          <Text style={styles.text} >010 583 3595, myynti@innogreen.fi</Text>
        </View>
        <View style={styles.footerContactWrapper}>
          <Text style={styles.textBold} >{`${t('common.outdoorSpaces')}:`}</Text>
          <Text style={styles.text} >010 583 3598, myynti@innogreen.fi</Text>
        </View>
      </View>

      <View style={styles.footerContactContainerRight}>
        <Text style={styles.text} >{t('project.pdfFooterText1')}</Text>
        <Text style={styles.text}>
        {t('project.pdfFooterText2')}&nbsp;
          {<Link src={`http://localhost:3000/project/${current_project.id}`} style={styles.link}>
            innogreen.fi
          </Link>}
        </Text>
      </View>
    </View>
  )

  const HeightSvg = () => (
    <Svg
      id="uuid-542e1b80-6220-4d03-8dbe-3e339877a1da"
      data-name="Layer 2"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 8.18 25"
      height="19"
      width="8.18"
      fill="none"
      stroke="black"
    >
      <G id="uuid-e4dee228-0f8e-4809-b77f-1e86f3250ae9" data-name="Layer 1">
        <G>
          <Line stroke='#000' fill='none' x1="4.09" y1="22.92" x2="4.09" y2="1.08"/>
          <Polygon points="8.18 19.6 7.45 18.92 4.09 22.53 .73 18.92 0 19.6 4.09 24 8.18 19.6" strokeWidth="0.3"/>
          <Polygon points="8.18 4.4 7.45 5.08 4.09 1.47 .73 5.08 0 4.4 4.09 0 8.18 4.4" strokeWidth="0.3"/>
        </G>
      </G>
    </Svg>
  )

  const WidthSvg = () => (
    <Svg
      id="uuid-edb48732-5856-4d9f-bf29-f0772f9dcb37"
      data-name="Layer 2"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 25 8.18"
      width="19"
      height="8.18"
      fill="none"
      stroke="black"
    >
      <G id="uuid-94f4d000-4e35-43af-9844-2e4128e28d1b" data-name="Layer 1">
        <G>
          <Line stroke='#000' fill='none' x1="1.08" y1="4.09" x2="22.92" y2="4.09"/>
          <Polygon points="4.4 8.18 5.08 7.45 1.47 4.09 5.08 .73 4.4 0 0 4.09 4.4 8.18" strokeWidth="0.3"/>
          <Polygon points="19.6 8.18 18.92 7.45 22.53 4.09 18.92 .73 19.6 0 24 4.09 19.6 8.18" strokeWidth="0.3"/>
        </G>
      </G>
    </Svg>
  )

  const CustomizeSvg = () => (
    <Svg
      id="uuid-60988aa4-a03a-466e-b583-60c374ff86e8"
      data-name="Layer 2"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 23 22.98"
      width="18"
      height="18"
      fill="none"
      stroke="black"
    >
      <G id="uuid-5ec54187-92ef-4e94-89bc-99bee8db6615" data-name="Layer 1">
        <G>
          <Line fill='none' strokeLinejoin='round' x1="5.69" y1="20.6" x2="2.39" y2="17.3"/>
          <Path fill='none' strokeLinejoin='round' d="M22.01,3.7l-2.71-2.71c-.65-.65-1.71-.65-2.36,0l-1.36,1.36,5.07,5.07,1.36-1.36c.65-.65.65-1.71,0-2.36Z"/>
          <Polygon fill='none' strokeLinejoin='round' points="20.56 7.46 8.43 19.61 .5 22.48 3.38 14.57 15.58 2.35"/>
          <Line fill='none' strokeLinejoin='round' x1="6.22" y1="16.77" x2="18.1" y2="4.87"/>
          <Line fill='none' strokeLinejoin='round' x1="8.72" y1="19.27" x2="3.73" y2="14.28"/>
        </G>
      </G>
    </Svg>
  )

  const ProjectHeader = () => (
    current_project && (
      <View style={styles.content} >
        <Text style={styles.projectTitle}>{current_project.name}</Text>
        <Text style={styles.projectDescription}>{current_project.description}</Text>
      </View>
    )
  )

  if (isLoading) {
    return (
      <div className='product-summary-pdf-container'>
        <LoadingSpinner/>
      </div>
    )
  }

  const ProductSummaryList = (product, p_index, length) => (
    <View style={styles.productEntryContainer} wrap={false}>
      <View style={styles.productEntry} >
        <View style={styles.productEntryLeftContent}>
          <Image style={styles.productEntryImg} src={product.imgSrc}></Image>
          <View style={styles.productEntryLeftContentProductInfo}>
            <Text style={styles.productEntryLeftContentProductInfoTitle}>
                {t(`${get_decoded_string(product.name)}.name`)}
            </Text>
            <View style={styles.productEntryLeftContentProductInfoCustomisation}>
              { group_by_customization_id(product.customizations).map((customization, c_index) => {
                  if (product?.name === 'Viherpysäkki') {
                    let configuration = null
                    if (i18n.exists(`${get_decoded_string(product.name)}.configuration`)) {
                      configuration = i18n.t(`${get_decoded_string(product.name)}.configuration`, { returnObjects: true })
                    }
                    return (
                      <React.Fragment key={c_index}>
                        <View style={styles.productEntryLeftContentProductInfoCustomisationEntry}>
                          <View style={styles.productEntryLeftContentProductInfoCustomisationEntryImgWrapper}>
                            { get_selected_customization_image(
                                customization.selected[0].customization_id,
                                customization.selected[0].selected_option_id, 'viherpysäkki')
                              ? 
                              (
                                <Image
                                  style={styles.productEntryLeftContentProductInfoCustomisationEntryImg}
                                  src={get_selected_customization_image(
                                    customization.selected[0].customization_id,
                                    customization.selected[0].selected_option_id, 'viherpysäkki')
                                  }>
                                </Image>
                              ) : (
                                <></>
                              )
                            }
                          </View>
                          <View style={styles.productEntryLeftContentProductInfoCustomisationEntryDescription}>
                            <Text style={styles.productEntryLeftContentProductInfoCustomisationEntryTextPurple}>{t(`products.${get_decoded_string(customization.name)}`)}</Text>
                            {customization.selected.map((item, itemIndex) => {
                                if (item.value === 0) {
                                  return 
                                } else {
                                  return (
                                    <React.Fragment key={itemIndex}>
                                      <Text style={styles.productEntryLeftContentProductInfoCustomisationEntryText}>{get_selected_customization(item, customization.selected_option_id, configuration[c_index], itemIndex)}</Text>
                                    </React.Fragment>
                                  )
                                }
                              })
                            }
                          </View>
                        </View>
                      </React.Fragment>
                    )
                  }
                  else if (product.name === 'Viherseinä') {
                    let configuration = null
                    if (i18n.exists(`${get_decoded_string(product.name)}.configurationProject`)) {
                      configuration = i18n.t(`${get_decoded_string(product.name)}.configurationProject`, { returnObjects: true })
                    }
                    return (
                      <React.Fragment key={c_index}>
                        <View style={styles.productEntryLeftContentProductInfoCustomisationEntry}>
                          <View style={styles.productEntryLeftContentProductInfoCustomisationEntryImgWrapper}>
                            {get_selected_customization_image(
                              customization.selected[0].customization_id,
                              customization.selected[0].selected_option_id) ? (
                              <Image
                                style={styles.productEntryLeftContentProductInfoCustomisationEntryImg}
                                src={get_selected_customization_image(
                                  customization.selected[0].customization_id,
                                  customization.selected[0].selected_option_id)
                                }>
                              </Image>
                            ) : (
                              <>
                                {HeightSvg()}
                              </>
                            )}
                          </View>
                          <View style={styles.productEntryLeftContentProductInfoCustomisationEntryDescription}>
                            <Text style={styles.productEntryLeftContentProductInfoCustomisationEntryTextPurple}>{t(`products.${get_decoded_string(customization.name)}`)}</Text>
                            {customization.name ===  "1. Leveys" ?
                              (
                                  get_selected_customization(customization, null, configuration[0])
                              ) : (
                                customization.selected.map((item, itemIndex) => {
                                  return (
                                    <React.Fragment key={itemIndex}>
                                      <Text style={styles.productEntryLeftContentProductInfoCustomisationEntryText}>{get_selected_customization(item, item.selected_option_id)}</Text>

                                    </React.Fragment>
                                  )
                                })
                              )
                            }
                          </View>
                        </View>
                      </React.Fragment>
                    )
                  } else if (product?.name === 'Räätälöitävä sammalseinä' || product?.name === 'Räätälöitävä viherseinä') {
                      let configuration = null
                      if (i18n.exists(`${get_decoded_string(product.name)}.configuration`)) {
                        configuration = i18n.t(`${get_decoded_string(product.name)}.configuration`, { returnObjects: true })
                      }
                      return (
                        <React.Fragment key={c_index}>
                          <View style={styles.productEntryLeftContentProductInfoCustomisationEntry}>
                            <View style={styles.productEntryLeftContentProductInfoCustomisationEntryImgWrapper}>
                              {configuration[c_index].configType === 'width' && (
                                <>
                                  {WidthSvg()}
                                </>
                              )}
                              {configuration[c_index].configType === 'height' && (
                                <>
                                  {HeightSvg()}
                                </>
                              )}
                              {configuration[c_index].configType === 'custom' && (
                                <>
                                  {CustomizeSvg()}
                                </>
                              )}
                            </View>
                            <View style={styles.productEntryLeftContentProductInfoCustomisationEntryDescription}>
                              <Text style={styles.productEntryLeftContentProductInfoCustomisationEntryTextPurple}>{t(`products.${get_decoded_string(customization.name)}`)}</Text>
                              {customization.selected.map((item, itemIndex) => {
                                  return (
                                    <React.Fragment key={itemIndex}>
                                      {configuration ? 
                                        <Text style={styles.productEntryLeftContentProductInfoCustomisationEntryText}>{get_selected_customization(item, item.selected_option_id, configuration[c_index])}</Text>
                                      :
                                        <Text style={styles.productEntryLeftContentProductInfoCustomisationEntryText}>{get_selected_customization(item, item.selected_option_id)}</Text>
                                      }
                                    </React.Fragment>
                                  )
                                })
                              }
                            </View>
                          </View>
                        </React.Fragment>
                      )
                  }
                  else {
                    return (
                      <React.Fragment key={c_index}>
                        <View style={styles.productEntryLeftContentProductInfoCustomisationEntry}>
                          <View style={styles.productEntryLeftContentProductInfoCustomisationEntryImgWrapper}>
                            { get_selected_customization_image(
                                customization.selected[0].customization_id,
                                customization.selected[0].selected_option_id) ?
                              (
                                <Image
                                  style={styles.productEntryLeftContentProductInfoCustomisationEntryImg}
                                  src={get_selected_customization_image(
                                    customization.selected[0].customization_id,
                                    customization.selected[0].selected_option_id)
                                  }>
                                </Image>
                              ) : (
                                <></>
                              )
                            }
                          </View>
                          <View style={styles.productEntryLeftContentProductInfoCustomisationEntryDescription}>
                            <Text style={styles.productEntryLeftContentProductInfoCustomisationEntryTextPurple}>{t(`products.${get_decoded_string(customization.name)}`)}</Text>
                            {customization.selected.map((item, itemIndex) => {
                                return (
                                  <React.Fragment key={itemIndex}>
                                    <Text style={styles.productEntryLeftContentProductInfoCustomisationEntryText}>{get_selected_customization(item, item.selected_option_id)}</Text>
                                  </React.Fragment>
                                )
                              })
                            }
                          </View>
                        </View>
                      </React.Fragment>
                    )
                  }
                })
              }
            </View>
          </View>
        </View>
        { group_by === '1' && (
          <View style={styles.productEntryRightContent}>
            <Text style={styles.productEntryRightContentTitle}>{`${product.spaces.reduce((sum, space) => sum + space.quantity, 0)} ${t('project.quantityShort')}`}</Text>
            { product.spaces.map((space, s_index) => {
                return (
                  <React.Fragment key={s_index}>
                    <View style={styles.productEntryRightContentSpaceContainer}>
                      <Text style={styles.productEntryRightContentSpaceText}>{space.quantity}</Text>
                      <Text style={styles.productEntryRightContentSpaceText}>{space.name}</Text>
                    </View>
                  </React.Fragment>
                )
              })
            }
          </View>
        )}
        { group_by === '2' && (
          <View style={styles.productEntryRightContent}>
            <Text style={styles.productEntryRightContentTitle}>{`${product.quantity} ${t('project.quantityShort')}`}</Text>
          </View>
        )}
        
      </View>
      {p_index !== length - 1 && <View style={styles.productEntryDivider}></View>}
    </View>
  )

  if (group_by === '1') {
    return (
      <div className='product-summary-pdf-container'>
          {/* View the PDF before printing */}
          {productGroups && current_project && (
            <PDFViewer style={{ width: '100%', height: '100dvh', border: 'none' }}>
              <Document title={current_project.name}>
                <Page wrap size={[900, 1274]} style={styles.page}>
                  {Header()}
                  {ProjectHeader()}
                  { productGroups.length > 0 ?
                    (
                      productGroups.map((product, p_index) => {
                        return (
                          <React.Fragment key={p_index}>
                            {ProductSummaryList(product, p_index, productGroups.length)}
                          </React.Fragment>
                        )
                      })
                    ) : (
                      <></>
                    )
                  }
                  {Footer()}
                </Page>
              </Document>
            </PDFViewer>
          )}
      </div>
    )
  } else if (group_by === '2') {
    return (
      <div className='product-summary-pdf-container'>
          {/* View the PDF before printing */}
          {productGroups && current_project && (
            <PDFViewer style={{ width: '100%', height: '100dvh', border: 'none' }}>
              <Document title={current_project.name}>
                <Page wrap size={[900, 1274]} style={styles.page}>
                  {Header()}
                  {ProjectHeader()}
                  { productGroups.length > 0 ?
                    (
                      productGroups.map((space, index) => {
                        return (
                          <React.Fragment key={index}>
                            <View style={styles.productSpaceContainer}>
                              <View style={styles.productSpaceLine}></View>
                              <Text style={styles.productSpaceText}>{space.space.name === 'DEFAULT SPACE' ? (t('project.defaultSpace')) : (space.space.name)}</Text>
                            </View>
                            { space.products.map((product, p_index) => {
                                return (
                                  <React.Fragment key={p_index}>
                                    {ProductSummaryList(product, p_index, space.products.length)}
                                  </React.Fragment>
                                )
                              })
                            }
                          </React.Fragment>
                        )
                      })
                    ) : (
                      <></>
                    )
                  }
                  {Footer()}
                </Page>
              </Document>
            </PDFViewer>
          )}
      </div>
    )
  }
}

export default ProductSummaryPDF